import {PerformanceLogger} from '../performance';
import {ENVIRONMENT_LEVEL} from '@/Constant';
import {accessTokenUrl, coreApiListForTime} from './coreApiList';
import {sum, min, max} from 'lodash-es';

const rteServer = process.env.REACT_APP_RTE_SERVER || '';

const PERFORMANCE_TYPE = {
    script: 'script',
    link: 'link',
    xmlhttprequest: 'xmlhttprequest',
};

const SAVE_KEY = {
    DELAY: 'delay',
    RTE_DOM_START_TIME: 'rteDomStartTime',
    FIRST_SHOWN_DELAY: 'firstShownDelay',
    BROWSER_RENDER_TIME: 'browserRenderTime',
    NORMALIZING_TIME: 'normalizingTime',
    DOM_BUILD_TIME: 'domBuildTime',
    WS_TIME: 'wsTime',
    LOAD_TYPE: 'loadType',
    EDITOR_MOUNT_TIME: 'editorMountTime',
    // event.fsNodesCount
    FS_NODES_COUNT: 'fsNodesCount',
    // event.hasCardNode
    HAS_CARD_NODE: 'hasCardNode',
    DOC_MODE: 'docMode',
} as const;

type SaveKey = typeof SAVE_KEY[keyof typeof SAVE_KEY];
interface Strategy {
    name: string;
    strategy: (performance: PerformanceResourceTiming) => boolean;
}


const correctStartTime = (window as any).wikiGlobal.htmlRecordTime;
// eslint-disable-next-line max-len
// const correctStartTime = (window.performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming).responseEnd;
export class LoadTimeDetailed {

    key = 'LoadTimeDetailed';

    logger: PerformanceLogger;

    lastLogTime: number = 0;

    loadType: string = 'hard';

    rteDomStartTime: number = 0;

    normalizingTime: number = 0;

    firstShownDelay: number = 0;

    browserRenderTime: number = 0;

    domBuildTime: number = 0;

    wsTime: number = 0;

    delay: number = 0;

    editorMountTime: number = 0;

    const = SAVE_KEY;
    fsNodesCount: number = 0;
    hasCardNode: number = 0;
    docMode: string = '';

    constructor(logger: PerformanceLogger) {
        this.logger = logger;
    }

    logWeb =({id, data}: {id: string, data?: any}) => {
        const logger = this.logger;
        const logLevel = logger?.logLevel || '';
        if ([ENVIRONMENT_LEVEL.online, ENVIRONMENT_LEVEL.gray, ENVIRONMENT_LEVEL.qa].includes(logLevel)) {
            logger.feLogger({
                id,
                data: {
                    ...data,
                },
            });
        }
    };

    getResourceAndApiPerformancesAfterLastLog = () => {
        if (!window.performance || !window.performance.getEntriesByType) {
            return [];
        }
        const performances = (window as any).performance.getEntriesByType('resource');
        return performances.filter((performance: PerformanceResourceTiming) => {
            const isAfterLastLog = performance.startTime >= this.lastLogTime;
            const isValidInitiatorType = [
                PERFORMANCE_TYPE.link,
                PERFORMANCE_TYPE.script,
                PERFORMANCE_TYPE.xmlhttprequest,
            ].includes(performance.initiatorType);
            return isAfterLastLog && isValidInitiatorType;
        });
    };

    getPerformancesByStrategy = (performances: PerformanceResourceTiming[], strategies: Strategy[]) => {
        const result: {
            [name: string]: PerformanceResourceTiming[];
        } = {};
        strategies.forEach(strategy => {
            result[strategy.name] = [];
        });
        performances.forEach((performance: PerformanceResourceTiming) => {
            strategies.forEach(strategy => {
                if (strategy.strategy(performance)) {
                    result[strategy.name].push(performance);
                }
            });
        });
        return result;
    };

    getCoreApiPerformancesStrategy = (performance: PerformanceResourceTiming) => {
        const isApi = performance.initiatorType === PERFORMANCE_TYPE.xmlhttprequest;
        if (!isApi) {
            return false;
        }
        const isCoreApi = coreApiListForTime.find(api => {
            // eslint-disable-next-line @typescript-eslint/prefer-regexp-exec
            return !!performance.name.match(api.url);
        });
        return !!isCoreApi;
    };

    getRteSourcePerformancesStrategy = (performance: PerformanceResourceTiming) => {
        const isValidInitiatorType = [
            PERFORMANCE_TYPE.link,
            PERFORMANCE_TYPE.script,
            PERFORMANCE_TYPE.xmlhttprequest,
        ].includes(performance.initiatorType);
        const isFromRteServer = performance.name.includes(rteServer);
        const isRteSourcePath = performance.name.includes('/fe/');
        return isValidInitiatorType && isFromRteServer && isRteSourcePath;
    };

    getRteApiPerformancesStrategy = (performance: PerformanceResourceTiming) => {
        const isValidInitiatorType = [
            PERFORMANCE_TYPE.xmlhttprequest,
        ].includes(performance.initiatorType);
        const isFromRteServer = performance.name.includes(rteServer);
        const isVersionApi = performance.name.includes('version.txt');
        return isValidInitiatorType && isFromRteServer && !isVersionApi;
    };

    getWikiSourcePerformancesStrategy = (performance: PerformanceResourceTiming) => {
        // rte 资源 + rte的version接口
        const isValidInitiatorType = [
            PERFORMANCE_TYPE.link,
            PERFORMANCE_TYPE.script,
        ].includes(performance.initiatorType);
        const isFromRteServer = performance.name.includes(location.host);
        const isVersionApi = (performance.initiatorType === PERFORMANCE_TYPE.xmlhttprequest)
            && performance.name.includes('version.txt');
        return (isValidInitiatorType && isFromRteServer) || isVersionApi;
    };

    getWikiRteTokenPerformancesStrategy = (performance: PerformanceResourceTiming) => {
        const isValidInitiatorType = [
            PERFORMANCE_TYPE.xmlhttprequest,
        ].includes(performance.initiatorType);
        const isAccessTokenUrl = performance.name.includes('/rteToken');
        return isValidInitiatorType && isAccessTokenUrl;
    };

    getDomCompleteTime = () => {
        // const navigationPerformance =
        // window.performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        // return navigationPerformance.domComplete - navigationPerformance.responseEnd;
        return performance.timing.domComplete - performance.timing.domLoading;
    };

    getSumOfPerformancesDuration = (performances: PerformanceResourceTiming[]) => {
        return sum(performances.map(performance => {
            return performance.duration;
        }));
    };

    getMergedDurationOfPerformances = (performances: PerformanceResourceTiming[]) => {
        const startTimes = performances.map((performance: PerformanceResourceTiming) => {
            return performance.fetchStart;
        });
        const endTimes = performances.map((performance: PerformanceResourceTiming) => {
            return performance.responseEnd;
        });
        return (max(endTimes) || 0) - (min(startTimes) || 0);
    };

    getLastRteTokenPerformance = (performances: PerformanceResourceTiming[]) => {
        return performances.sort((performances1, performances2) => {
            return performances2.responseEnd - performances1.responseEnd;
        })[0];
    };

    getRteTime = (editorMountTime: number, rteDomStartTime: number) => {
        return rteDomStartTime - editorMountTime;
    };

    getWikiTime = (editorMountTime: number) => {
        if (this.lastLogTime) {
            return editorMountTime - this.lastLogTime;
        }
        return editorMountTime - correctStartTime;
    };

    save = (key: SaveKey, value: number | string) => {
        (this[key]) = value;
    };

    batchSave = (value: {[key: string]: number | string}) => {
        Object.keys(value).forEach(key => {
            (this[key as keyof LoadTimeDetailed]) = value[key];
        });
    };

    updateLastLogTime = () => {
        this.lastLogTime = performance.now();
    };

    getDetails = (performances: PerformanceResourceTiming[]) => {
        return performances.map((performance: PerformanceResourceTiming) => {
            return {
                name: performance.name,
                startTime: performance.startTime,
                duration: performance.duration,
                endTime: performance.responseEnd,
                fetchStart: performance.fetchStart,
            };
        }).sort((performances1, performances2) => {
            return performances1.startTime - performances2.startTime;
        });
    };

    // 详细信息上报
    log = ({
        // shownDelay,
        nodesCount = 0,
        // 编辑器内容 不关心
        nodesDetail,
    }: {
        // shownDelay: number;
        nodesCount: number;
        // 编辑器内容 不关心
        nodesDetail: any;
    }) => {
        try {
            const normalizingTime = this.normalizingTime || 0;
            const firstShownDelay = this.firstShownDelay;
            const domBuildTime = this.domBuildTime;
            const browserRenderTime = this.browserRenderTime;
            const wsTime = this.wsTime;
            const performances = this.getResourceAndApiPerformancesAfterLastLog();
            const wikiDomCompleteTime = this.getDomCompleteTime();
            const strategies = [
                {
                    name: 'coreApiPerformances',
                    strategy: this.getCoreApiPerformancesStrategy,
                },
                {
                    name: 'rteSourceLoadTimePerformances',
                    strategy: this.getRteSourcePerformancesStrategy,
                },
                {
                    name: 'rteApiLoadTimePerformances',
                    strategy: this.getRteApiPerformancesStrategy,
                },
                {
                    name: 'wikiSourceLoadTimePerformances',
                    strategy: this.getWikiSourcePerformancesStrategy,
                },
                {
                    name: 'wikiRteTokenPerformances',
                    strategy: this.getWikiRteTokenPerformancesStrategy,
                },
            ];
            const {
                coreApiPerformances,
                rteSourceLoadTimePerformances,
                rteApiLoadTimePerformances,
                // wikiSourceLoadTimePerformances,
                // wikiRteTokenPerformances,
            } = this.getPerformancesByStrategy(performances, strategies);
            // accessToken并联 不记录在接口总时间内 但是需要放在detail里以供分析
            const coreApiTime = this.getSumOfPerformancesDuration(
                coreApiPerformances.filter((performance: PerformanceResourceTiming) => {
                    return !performance.name.includes(accessTokenUrl);
                })
            );
            const rteSourceLoadTime = this.getMergedDurationOfPerformances(rteSourceLoadTimePerformances);
            const rteApiLoadTime = this.getSumOfPerformancesDuration(rteApiLoadTimePerformances);
            // const wikiSourceLoadTime = this.getSumOfPerformancesDuration(wikiSourceLoadTimePerformances);
            const rteTime = this.getRteTime(this.editorMountTime, this.rteDomStartTime);
            const wikiTime = this.getWikiTime(this.editorMountTime);
            // 进入编辑器状态切换时也会触发 需要过滤掉
            if (!wikiTime || !this.delay) {
                return;
            }
            const uploadData = {
                // details: this.getDetails([
                //     ...wikiSourceLoadTimePerformances,
                //     ...coreApiPerformances,
                //     ...rteSourceLoadTimePerformances,
                //     ...rteApiLoadTimePerformances,
                // ]),
                type: this.loadType,
                delay: this.delay,
                firstShownDelay,
                wikiTime,
                wikiCoreApiTime: coreApiTime,
                wikiSourceTime: this.loadType === 'hard' ? wikiDomCompleteTime : 0,
                rteTime,
                rteSourceTime: rteSourceLoadTime,
                rteApiTime: rteApiLoadTime,
                rteWSTime: wsTime,
                editorTime: normalizingTime + domBuildTime + browserRenderTime,
                editorNormalizeTime: normalizingTime,
                editorDocBuildTime: domBuildTime,
                editorBrowserRenderTime: browserRenderTime,
                editorNodesCount: nodesCount,
                editorNodesDetail: nodesDetail,
                fsNodesCount: this.fsNodesCount,
                hasCardNode: this.hasCardNode,
                docMode: this.docMode,
            };
            // console.log('uploadData: ', uploadData);
            // console.log(
            //     'uploadData: ',
            //     wikiTime + rteTime + uploadData.editorTime,
            //     firstShownDelay,
            //     wikiTime + rteTime + uploadData.editorTime - firstShownDelay
            // );

            this.logWeb({
                id: '80001.2.42',
                data: uploadData,
            });
        } catch (e) {
            console.error(e);
        }
    };

}
